import React, { useState } from 'react';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authAPI } from '../services/apiService';
import { Mail, ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const [userId, setUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
        toast.success('Password reset link sent to your email!');
      } else {
        toast.error(data.detail || 'User ID not found');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <Card className="w-full max-w-md border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
          <CardTitle className="text-3xl font-bold" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Forgot Password?
          </CardTitle>
          <CardDescription className="text-base">
            Enter your Username and we'll send a password reset link to your registered email.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!emailSent ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="userId" className="text-gray-700 font-semibold">Username</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <Input
                  id="userId"
                  name="userId"
                  type="text"
                  placeholder="Enter your username (e.g., pravin123)"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  required
                  className="h-12 pl-10"
                />
              </div>
              <p className="text-xs text-emerald-600 font-medium mt-1">
                ✓ Simple username provided when your account was created
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-white font-bold text-lg border-2 border-yellow-400 shadow-lg"
              style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                <p className="text-gray-700 font-semibold mb-2">✅ Email Sent Successfully!</p>
                <p className="text-sm text-gray-600">
                  A password reset link has been sent to your registered email address.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-gray-600">
                <p><strong>From:</strong> notification@netixfibernet.com</p>
                <p className="mt-1"><strong>Link expires:</strong> 1 hour</p>
              </div>
            </div>
          )}
        </CardContent>
        
        <div className="px-6 pb-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Remember your password?{' '}
              <Link to="/customer-login" className="text-red-600 hover:text-red-700 font-semibold">
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </Card>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
